import { useRoute, useState } from '#imports'
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { removeEmptyValuesInObject } from '@backmarket/utils/object/removeEmptyValuesInObject'

import { useUrlParams } from '~/scopes/product-core/composables/useUrlParams/useUrlParams'
import {
  BACKBOX_GRADE_LABELS,
  type BackboxGradeLabel,
} from '~/scopes/product-core/pages/ProductDetailPage/us/composables/useFetchReviewsPerGrade/useFetchReviewsPerGrade.constants'
import type { ReviewsPerGrade } from '~/scopes/product-core/pages/ProductDetailPage/us/composables/useFetchReviewsPerGrade/useFetchReviewsPerGrade.types'
import type { DisplayedReviewWithAttachments } from '~/scopes/reviews/reviews-display/models/reviews'

import {
  DEFAULT_GRADE_IMAGE_CATEGORY,
  GRADE_DEFAULT_IMAGES_BY_CATEGORY,
  GRADE_LABEL_BY_VALUE,
} from '../ReviewPhotosGuidance.constants'
import translations from '../ReviewPhotosGuidance.translations'
import type {
  FormattedReview,
  GradePictures,
  PictureItem,
  ReviewSlide,
} from '../components/ReviewPhotosGuidanceDrawer/ReviewPhotosGuidanceDrawer.types'

export const REVIEW_PHOTOS_GUIDANCE_DRAWER_NAME =
  'review-photos-guidance-drawer' as const

const MAX_REVIEW_PICTURES = 20

/**
 * All grade labels, each with a flat list of pictures. Every grade starts
 * with the default grade image as the first slide (index 0), followed by
 * review photos. The default image slide has `review: null` so the right
 * side can show a default content card instead of a review.
 */
export function useReviewPhotosGuidance(props: {
  trackingModel?: string
  categoryId?: string
  reviewsPerGrade?: ReviewsPerGrade | null
}) {
  const route = useRoute()
  const { grade } = useUrlParams()
  const i18n = useI18n()
  const locale = useI18nLocale()
  const { trackClick } = useTracking()

  const targetGrade = useState<BackboxGradeLabel | null>(
    'review-photos-guidance-target-grade',
    () => null,
  )
  const targetPictureIndex = useState<number>(
    'review-photos-guidance-target-picture-index',
    () => 0,
  )

  const selectedGradeLabel = computed<BackboxGradeLabel | undefined>(() => {
    if (grade.value === undefined || grade.value === null) return undefined

    return GRADE_LABEL_BY_VALUE[grade.value]
  })

  const gradeDefaultImages = computed<
    Record<BackboxGradeLabel, string> | undefined
  >(() => {
    return (
      GRADE_DEFAULT_IMAGES_BY_CATEGORY[
        props.categoryId ?? DEFAULT_GRADE_IMAGE_CATEGORY
      ] ?? GRADE_DEFAULT_IMAGES_BY_CATEGORY[DEFAULT_GRADE_IMAGE_CATEGORY]
    )
  })

  const defaultImageUrl = computed<string | null>(() => {
    if (!selectedGradeLabel.value) return null

    return gradeDefaultImages.value?.[selectedGradeLabel.value] ?? null
  })

  const GRADE_NAME_BY_LABEL: Record<BackboxGradeLabel, string> = {
    PREMIUM: i18n(translations.tabPremium),
    FLAWLESS: i18n(translations.tabFlawless),
    VERY_GOOD: i18n(translations.tabVeryGood),
    ECO: i18n(translations.tabEco),
  }

  const gradeName = computed(() =>
    selectedGradeLabel.value
      ? GRADE_NAME_BY_LABEL[selectedGradeLabel.value]
      : '',
  )

  const carouselTitle = computed(() =>
    selectedGradeLabel.value
      ? i18n(translations.gradeTitle, { grade: gradeName.value })
      : '',
  )

  const exampleTag = computed(() =>
    selectedGradeLabel.value
      ? i18n(translations.exampleTag, { grade: gradeName.value })
      : '',
  )

  const noReviewsCardTitle = computed(() =>
    selectedGradeLabel.value
      ? i18n(translations.noReviewsCardTitle, { grade: gradeName.value })
      : '',
  )

  function formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleDateString(locale, {
      month: 'short',
      year: 'numeric',
    })
  }

  function toFormattedReview(
    review: DisplayedReviewWithAttachments,
  ): FormattedReview {
    return {
      ...review,
      reviewDate: review.createdAt,
      purchaseDate: review.product.purchasedAt,
      pictures: review.attachments.map(({ url }) => url),
    }
  }

  function selectReviewPictures(
    reviews: DisplayedReviewWithAttachments[],
  ): PictureItem[] {
    const reviewsWithAttachments = reviews.filter(
      (review) => review.attachments && review.attachments.length > 0,
    )

    const selectedByReview: PictureItem[][] = reviewsWithAttachments.map(
      () => [],
    )
    const attachmentCounts = reviewsWithAttachments.map(
      (review) => review.attachments.length,
    )

    let totalSelected = 0
    let attachmentIndex = 0

    while (totalSelected < MAX_REVIEW_PICTURES) {
      let addedThisRound = false

      for (let i = 0; i < reviewsWithAttachments.length; i += 1) {
        if (totalSelected >= MAX_REVIEW_PICTURES) break
        if (attachmentIndex >= attachmentCounts[i]) continue

        const review = reviewsWithAttachments[i]
        const attachment = review.attachments[attachmentIndex]
        const formatted = toFormattedReview(review)

        selectedByReview[i].push({
          id: `${review.id}-${attachmentIndex}`,
          url: attachment.url,
          review: formatted,
        })

        totalSelected += 1
        addedThisRound = true
      }

      if (!addedThisRound) break

      attachmentIndex += 1
    }

    return selectedByReview.flat()
  }

  /**
   * All grade labels, each with a flat list of pictures. Every grade starts
   * with the default grade image as the first slide (index 0), followed by
   * review photos. The default image slide has `review: null` so the right
   * side can show a default content card instead of a review.
   */
  const gradesWithPictures = computed<GradePictures[]>(() => {
    if (!props.reviewsPerGrade) return []

    return BACKBOX_GRADE_LABELS.flatMap((gradeLabel) => {
      const defaultImage = gradeDefaultImages.value?.[gradeLabel]
      if (!defaultImage) return []

      const defaultPicture: PictureItem = {
        id: `default-${gradeLabel}`,
        url: defaultImage,
        review: null,
      }

      const reviews = props.reviewsPerGrade?.[gradeLabel] ?? []

      const reviewPictures = selectReviewPictures(
        reviews as DisplayedReviewWithAttachments[],
      )

      return [{ gradeLabel, pictures: [defaultPicture, ...reviewPictures] }]
    })
  })

  const hasReviewsWithAttachments = computed(
    () => gradesWithPictures.value.length > 0,
  )

  const selectedGradePictures = computed<GradePictures | undefined>(() => {
    return gradesWithPictures.value.find(
      ({ gradeLabel }) => gradeLabel === selectedGradeLabel.value,
    )
  })

  const reviewSlides = computed<ReviewSlide[]>(() => {
    const gradePictures = selectedGradePictures.value
    if (!gradePictures) return []

    return gradePictures.pictures
      .map((picture, index) => ({ picture, index }))
      .filter(({ picture }) => picture.review !== null)
      .map(({ picture, index }) => {
        const review = picture.review as FormattedReview
        const firstName = review.customer?.firstName ?? null
        const lastName = review.customer?.lastName ?? null
        const customerName =
          firstName || lastName
            ? [firstName, lastName].filter(Boolean).join(' ')
            : null

        return {
          id: picture.id,
          imageUrl: picture.url,
          title: review.product.title,
          customerName,
          reviewDate: review.createdAt ? formatDate(review.createdAt) : null,
          gradeLabel: gradePictures.gradeLabel,
          pictureIndex: index,
        }
      })
  })

  function handleReviewClick(openModalFn: () => void, index: number) {
    if (index === 0) {
      const gradeLabel =
        selectedGradeLabel.value ?? gradesWithPictures.value[0]?.gradeLabel

      if (gradeLabel) {
        targetGrade.value = gradeLabel
        targetPictureIndex.value = 0
      }
      openModalFn()

      return
    }

    const slide = reviewSlides.value[index - 1]
    if (!slide) return

    targetGrade.value = slide.gradeLabel
    targetPictureIndex.value = slide.pictureIndex
    openModalFn()

    trackClick(
      removeEmptyValuesInObject({
        page_type: route.name as string,
        position: index,
        product_model: props.trackingModel || slide.imageUrl,
        zone: 'review_photo_guidance',
      }),
    )
  }

  return {
    targetGrade,
    targetPictureIndex,
    selectedGradeLabel,
    defaultImageUrl,
    gradesWithPictures,
    hasReviewsWithAttachments,
    reviewSlides,
    gradeName,
    carouselTitle,
    exampleTag,
    noReviewsCardTitle,
    handleReviewClick,
  }
}
